import type { CSSProperties } from 'react';
import type { GridApi, IRowNode } from 'ag-grid-community';
import type { ColDef } from '@dataGrid/MasterDetails';
import {
  DETAIL_GRID_BORDER_HEIGHT,
  DETAIL_GRID_TITLE,
  DETAIL_HEADER_HEIGHT,
  DETAIL_MAX_BODY_HEIGHT,
  DETAIL_PANEL_MAX_HEIGHT,
  DETAIL_PANEL_VERTICAL_PADDING,
  DETAIL_ROW_HEIGHT,
} from './constants';
import type { SwapBoardGroup, SwapBoardTrailer } from './types';
import type { AppTheme } from './styles';
import { normalizePickupId, normalizeShipperCode, pickupIdsMatch } from './idHelpers';

export { pickupIdsMatch };

export const applyDetailGridSelection = ({
  api,
  activePickupId,
}: {
  api: GridApi | null | undefined;
  activePickupId?: string | null;
}) => {
  if (!api) {
    return;
  }

  if (!activePickupId) {
    api.deselectAll?.();
    return;
  }

  const current = api.getSelectedRows?.()?.[0] as SwapBoardTrailer | undefined;
  if (pickupIdsMatch(current?.pickupId, activePickupId)) {
    return;
  }

  api.deselectAll?.();
  api.forEachNode?.((node: IRowNode) => {
    if (pickupIdsMatch((node.data as SwapBoardTrailer)?.pickupId, activePickupId)) {
      node.setSelected(true, false, 'api');
    }
  });
};

export const computeDetailGridContentHeight = (rowCount: number) =>
  DETAIL_HEADER_HEIGHT + rowCount * DETAIL_ROW_HEIGHT;

export const computeDetailGridHeight = (rowCount: number) =>
  Math.min(computeDetailGridContentHeight(rowCount), DETAIL_MAX_BODY_HEIGHT);

export const computeDetailPanelHeight = (rowCount: number) =>
  computeDetailGridHeight(rowCount) + DETAIL_PANEL_VERTICAL_PADDING + DETAIL_GRID_BORDER_HEIGHT;

export const getDetailGridSizing = (rowCount: number) => {
  const gridContentHeight = computeDetailGridContentHeight(rowCount);
  const gridHeight = computeDetailGridHeight(rowCount);
  const panelHeight = computeDetailPanelHeight(rowCount);
  const useAutoHeight = gridContentHeight <= DETAIL_MAX_BODY_HEIGHT;
  return { gridContentHeight, gridHeight, panelHeight, useAutoHeight };
};

export const buildDetailGridConfig = ({
  defaultColDef,
  columnDefs,
}: {
  defaultColDef: ColDef;
  columnDefs: ColDef[];
}) => ({
  title: DETAIL_GRID_TITLE,
  keyColumn: 'pickupId',
  rowSelection: 'single' as const,
  rowDeselection: true,
  defaultColDef,
  columns: columnDefs,
});

export const getDetailGridBorderStyle = (theme?: AppTheme | null): CSSProperties => ({
  border: theme?.gridGroupHeaderBorderColor
    ? `1px solid ${theme.gridGroupHeaderBorderColor}`
    : '1px solid #ccc',
  borderRadius: '2px',
  boxSizing: 'border-box',
  overflow: 'hidden',
});

export type DetailPanelLayoutStyleParams = {
  leftOffset: number;
  panelHeight: number;
  borderColor?: string;
};

export const getDetailPanelLayoutStyle = ({
  leftOffset,
  panelHeight,
  borderColor,
}: DetailPanelLayoutStyleParams): CSSProperties => ({
  height: `${panelHeight}px`,
  maxHeight: `${DETAIL_PANEL_MAX_HEIGHT}px`,
  padding: `10px 10px 10px ${leftOffset}px`,
  boxSizing: 'border-box',
  overflow: 'hidden',
  borderBottom: borderColor ? `1px solid ${borderColor}` : undefined,
});

export const findLocalPickupIdMatch = (
  globalSelectedPickupId: string | null | undefined,
  localPickupIds: string[]
) => {
  if (!globalSelectedPickupId) {
    return null;
  }

  const normalizedGlobal = normalizePickupId(globalSelectedPickupId);
  return (
    localPickupIds.find((pickupId) => normalizePickupId(pickupId) === normalizedGlobal) ?? null
  );
};

export type GetTrailersForShipperGroupParams = {
  swapTrailers: SwapBoardGroup[] | undefined;
  shipperCode?: string | null;
  fallbackTrailers?: SwapBoardTrailer[];
};

export const getTrailersForShipperGroup = ({
  swapTrailers,
  shipperCode,
  fallbackTrailers = [],
}: GetTrailersForShipperGroupParams) => {
  const normalizedShipperCode = normalizeShipperCode(shipperCode);
  if (!normalizedShipperCode || !Array.isArray(swapTrailers)) {
    return fallbackTrailers;
  }

  const group = swapTrailers.find(
    (swapBoardGroup) => normalizeShipperCode(swapBoardGroup.rmaxShipperCode) === normalizedShipperCode
  );

  return group?.trailers ?? fallbackTrailers;
};
